import { toPlainMessage, type PlainMessage } from '@bufbuild/protobuf';

import api from '@/shared/api/api';
import type { DocumentTemplateIssue } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import { BASE_URI } from '@/shared/config/config';

import type {
  DocumentTemplate,
  DocumentTemplateParseRequest,
  DocumentTemplateParsedSection,
  DocumentTemplateParseResponse,
  DocumentTemplateSummary,
  EncounterSchema,
  SchemaDocumentRef,
} from '@/entities/templates/config';

import {
  catalogStatusesToWire,
  catalogStatusToWire,
  cloneResultToDomain,
  communityTemplateToDomain,
  documentTemplateSummaryToDomain,
  documentTemplateToDomain,
  documentTypeToDomain,
  documentTypeToWire,
  encounterSchemaSummaryToDomain,
  encounterSchemaToDomain,
  schemaDocumentRefToWire,
} from './scribeCatalogConv';
import type {
  CatalogCloneResult,
  CatalogStatusName,
  CommunityTemplateDetailResponse,
} from './scribeCatalogTypes';
import { callScribe } from './scribeError';
import { fetchAllCatalogPages } from './scribePaging';

// Template parsing is not part of the catalog contract and keeps talking to the
// REST surface until the parsing feature itself is reworked.
const PARSE_API_BASE = `${BASE_URI}/api/scribe`;

export type { CatalogCloneResult, CommunityTemplateDetailResponse };

// === Request types ===

// A clinic template and the newer community version scribe offers it.
export type CommunityVersionRequest = {
  templateId: string;
  communityTemplateId: string;
};

export type ListDocumentTemplatesParams = {
  organization_id: string;
  language?: string;
  // Empty means active templates only, which is what most screens want.
  statuses?: CatalogStatusName[];
  type?: string;
};

export type CreateDocumentTemplateRequest = {
  code: string;
  language: string;
  name: string;
  tab_title: string;
  sections: DocumentTemplate['sections'];
  organization_id: string;
  type?: string;
  description?: string | null;
  source_kind?: 'system' | 'community' | 'organization';
  derived_from_kind?: 'system' | 'community' | null;
  derived_from_id?: string | null;
  base_series_id?: string;
  // With base_series_id: the version the change started from. Scribe refuses
  // the write when another version replaced it meanwhile.
  expected_latest_template_id?: string;
  status?: string;
  source_document?: string | null;
};

export type UpdateDocumentTemplateDraftRequest = Pick<
  CreateDocumentTemplateRequest,
  | 'code'
  | 'language'
  | 'name'
  | 'tab_title'
  | 'sections'
  | 'type'
  | 'description'
  | 'source_document'
>;

export type RestoreDocumentTemplateRequest = {
  code?: string;
  name?: string;
};

export type CloneDocumentTemplateRequest = {
  target_organization_id: string;
};

// Block templates come back with scribe's non-blocking `warning_*` issues.
export type CreatedDocumentTemplate = {
  template: DocumentTemplate;
  warnings: PlainMessage<DocumentTemplateIssue>[];
};

export type DocumentTemplateListResponse = {
  document_templates: DocumentTemplateSummary[];
  total: number;
};

export type ListEncounterSchemasParams = {
  organization_id: string;
  language?: string;
  include_draft?: boolean;
};

export type EncounterSchemaListResponse = {
  schemas: EncounterSchema[];
  total: number;
};

export type SchemaDocumentRefInput = Pick<
  SchemaDocumentRef,
  'template_series_id' | 'inclusion'
>;

export type CreateEncounterSchemaRequest = {
  code: string;
  language: string;
  name: string;
  organization_id: string;
  documents: SchemaDocumentRefInput[];
  description?: string;
  status?: string;
  analytics_model_series_id?: string;
};

export type UpdateEncounterSchemaRequest = {
  code?: string;
  language?: string;
  name?: string;
  description?: string;
  // An empty value unlinks the analytics model; omitting the field keeps it.
  analytics_model_series_id?: string;
  documents?: SchemaDocumentRefInput[];
  status?: string;
};

export type RestoreEncounterSchemaRequest = {
  code?: string;
  name?: string;
};

export type PublishCommunityTemplateRequest = {
  template_id: string;
  specialty: string;
  category: string;
  description?: string;
  tags?: string[];
  location?: string;
};

export type ImportCommunityTemplateRequest = {
  organization_id: string;
  code?: string;
  name?: string;
};

export type ImportCommunityTemplateResponse = {
  community_template_id: string;
  document_template_id: string;
  document_template_series_id: string;
  reused_existing: boolean;
};

// === Template parsing (REST) ===

// The parse endpoint answers in scribe's own casing for document and section
// types, which the template form keeps in upper case.
type ParsedTemplate = Omit<
  DocumentTemplateParseResponse,
  'type' | 'sections'
> & {
  type?: string;
  sections: (Omit<DocumentTemplateParsedSection, 'processing_type'> & {
    processing_type?: string;
  })[];
};

const parsedProcessingTypeToForm = (
  processingType?: string,
): DocumentTemplateParsedSection['processing_type'] =>
  processingType
    ? (processingType.toUpperCase() as DocumentTemplateParsedSection['processing_type'])
    : undefined;

const parseRequest = async <T>(
  path: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await fetch(`${PARSE_API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    const payload = (() => {
      try {
        return JSON.parse(errorText);
      } catch {
        return null;
      }
    })();

    throw new Error(
      payload?.detail ||
        payload?.error ||
        payload?.message ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return (await response.json()) as T;
};

// === API client ===

export const templatesApi = {
  // === Document templates ===

  listDocumentTemplates: async (
    params: ListDocumentTemplatesParams,
  ): Promise<DocumentTemplateListResponse> => {
    const page = await fetchAllCatalogPages(async (Offset, Limit) => {
      const response = await callScribe(
        api.documentTemplate.list({
          OrganizationID: params.organization_id,
          Language: params.language,
          Statuses: catalogStatusesToWire(params.statuses),
          Type: documentTypeToWire(params.type),
          Offset,
          Limit,
        }),
      );

      return {
        items: response.DocumentTemplates.map(documentTemplateSummaryToDomain),
        total: response.Total,
      };
    });

    return { document_templates: page.items, total: page.total };
  },

  createDocumentTemplate: async (
    data: CreateDocumentTemplateRequest,
  ): Promise<CreatedDocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.create({
        OrganizationID: data.organization_id,
        Code: data.code,
        Language: data.language,
        Type: documentTypeToWire(data.type),
        Name: data.name,
        TabTitle: data.tab_title,
        Description: data.description ?? undefined,
        Sections: data.sections,
        BaseSeriesID: data.base_series_id,
        ExpectedLatestTemplateID: data.expected_latest_template_id,
        SourceKind: data.source_kind,
        DerivedFromKind: data.derived_from_kind ?? undefined,
        DerivedFromID: data.derived_from_id ?? undefined,
        Status: catalogStatusToWire(data.status),
        SourceDocument: data.source_document ?? undefined,
      }),
    );

    return {
      template: documentTemplateToDomain(response.DocumentTemplate),
      warnings: response.Warnings.map((warning) => toPlainMessage(warning)),
    };
  },

  updateDocumentTemplateDraft: async (
    id: string,
    data: UpdateDocumentTemplateDraftRequest,
  ): Promise<DocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.updateDraft({
        TemplateID: id,
        Code: data.code,
        Language: data.language,
        Type: documentTypeToWire(data.type),
        Name: data.name,
        TabTitle: data.tab_title,
        Description: data.description ?? undefined,
        Sections: data.sections,
        SourceDocument: data.source_document ?? undefined,
      }),
    );

    return documentTemplateToDomain(response.DocumentTemplate);
  },

  getDocumentTemplate: async (
    id: string,
    organizationId?: string,
  ): Promise<DocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.get({
        TemplateID: id,
        OrganizationID: organizationId,
      }),
    );

    return documentTemplateToDomain(response.DocumentTemplate);
  },

  getDocumentTemplateBySeriesId: async (
    seriesId: string,
    organizationId?: string,
  ): Promise<DocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.getBySeriesID({
        SeriesID: seriesId,
        OrganizationID: organizationId,
      }),
    );

    return documentTemplateToDomain(response.DocumentTemplate);
  },

  // Resolves the newest version of a series regardless of status — the base
  // a proposal redline diffs against. A draft-only series has no active
  // version, so the active-only getBySeriesID cannot serve that case.
  getDocumentTemplateSeriesHead: async (
    seriesId: string,
    organizationId?: string,
  ): Promise<DocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.listVersions({
        SeriesID: seriesId,
        OrganizationID: organizationId,
      }),
    );

    const newest = (response.DocumentTemplates ?? []).reduce<
      (typeof response.DocumentTemplates)[number] | undefined
    >(
      (head, version) =>
        !head || (version.Version ?? 0) > (head.Version ?? 0) ? version : head,
      undefined,
    );
    if (!newest) {
      throw new Error('template series has no versions');
    }

    return documentTemplateToDomain(newest);
  },

  deleteDocumentTemplate: async (id: string): Promise<void> => {
    await callScribe(api.documentTemplate.delete({ TemplateID: id }));
  },

  restoreDocumentTemplate: async (
    id: string,
    data?: RestoreDocumentTemplateRequest,
  ): Promise<void> => {
    await callScribe(
      api.documentTemplate.restore({
        TemplateID: id,
        Code: data?.code,
        Name: data?.name,
      }),
    );
  },

  cloneDocumentTemplate: async (
    id: string,
    data: CloneDocumentTemplateRequest,
  ): Promise<CatalogCloneResult> => {
    const response = await callScribe(
      api.documentTemplate.clone({
        OrganizationID: data.target_organization_id,
        Overrides: [{ Family: 'document_template', SourceAssetID: id }],
      }),
    );

    return cloneResultToDomain(response.Result);
  },

  parseDocumentTemplate: async (
    data: DocumentTemplateParseRequest,
    signal?: AbortSignal,
  ): Promise<DocumentTemplateParseResponse> => {
    const parsed = await parseRequest<ParsedTemplate>(
      '/document-templates/parse',
      {
        method: 'POST',
        body: JSON.stringify(data),
        signal,
      },
    );

    return {
      ...parsed,
      type: parsed.type ? documentTypeToDomain(parsed.type) : undefined,
      sections: parsed.sections.map((section) => ({
        ...section,
        processing_type: parsedProcessingTypeToForm(section.processing_type),
      })),
    };
  },

  // === Encounter schemas ===

  listEncounterSchemas: async (
    params: ListEncounterSchemasParams,
  ): Promise<EncounterSchemaListResponse> => {
    const page = await fetchAllCatalogPages(async (Offset, Limit) => {
      const response = await callScribe(
        api.encounterSchema.list({
          OrganizationID: params.organization_id,
          Language: params.language,
          IncludeDraft: params.include_draft,
          Offset,
          Limit,
        }),
      );

      return {
        items: response.Schemas.map(encounterSchemaSummaryToDomain),
        total: response.Total,
      };
    });

    return { schemas: page.items, total: page.total };
  },

  createEncounterSchema: async (
    data: CreateEncounterSchemaRequest,
  ): Promise<EncounterSchema> => {
    const response = await callScribe(
      api.encounterSchema.create({
        OrganizationID: data.organization_id,
        Code: data.code,
        Language: data.language,
        Name: data.name,
        Description: data.description,
        AnalyticsModelSeriesID: data.analytics_model_series_id,
        Documents: data.documents.map(schemaDocumentRefToWire),
        Status: catalogStatusToWire(data.status),
      }),
    );

    return encounterSchemaToDomain(response.Schema);
  },

  getEncounterSchema: async (
    id: string,
    organizationId?: string,
  ): Promise<EncounterSchema> => {
    const response = await callScribe(
      api.encounterSchema.get({ SchemaID: id, OrganizationID: organizationId }),
    );

    return encounterSchemaToDomain(response.Schema);
  },

  updateEncounterSchema: async (
    id: string,
    data: UpdateEncounterSchemaRequest,
  ): Promise<EncounterSchema> => {
    const response = await callScribe(
      api.encounterSchema.update({
        SchemaID: id,
        Code: data.code,
        Language: data.language,
        Name: data.name,
        Description: data.description,
        AnalyticsModelSeriesID: data.analytics_model_series_id,
        Documents: data.documents && {
          Documents: data.documents.map(schemaDocumentRefToWire),
        },
        Status: catalogStatusToWire(data.status),
      }),
    );

    return encounterSchemaToDomain(response.Schema);
  },

  deleteEncounterSchema: async (id: string): Promise<void> => {
    await callScribe(api.encounterSchema.delete({ SchemaID: id }));
  },

  restoreEncounterSchema: async (
    id: string,
    data?: RestoreEncounterSchemaRequest,
  ): Promise<void> => {
    await callScribe(
      api.encounterSchema.restore({
        SchemaID: id,
        Code: data?.code,
        Name: data?.name,
      }),
    );
  },

  cloneEncounterSchema: async (
    id: string,
    data: { target_organization_id: string },
  ): Promise<CatalogCloneResult> => {
    const response = await callScribe(
      api.encounterSchema.clone({
        OrganizationID: data.target_organization_id,
        Overrides: [{ Family: 'encounter_schema', SourceAssetID: id }],
      }),
    );

    return cloneResultToDomain(response.Result);
  },

  // === Community templates ===

  getCommunityTemplate: async (
    id: string,
    organizationId?: string,
  ): Promise<CommunityTemplateDetailResponse> => {
    const response = await callScribe(
      api.documentTemplate.getCommunity({
        TemplateID: id,
        OrganizationID: organizationId,
      }),
    );

    return communityTemplateToDomain(response.Template);
  },

  importCommunityTemplate: async (
    id: string,
    data: ImportCommunityTemplateRequest,
  ): Promise<ImportCommunityTemplateResponse> => {
    const response = await callScribe(
      api.documentTemplate.importCommunity({
        TemplateID: id,
        OrganizationID: data.organization_id,
        Code: data.code,
        Name: data.name,
      }),
    );

    return {
      community_template_id: response.CommunityTemplateID,
      document_template_id: response.DocumentTemplateID,
      document_template_series_id: response.DocumentTemplateSeriesID,
      reused_existing: response.ReusedExisting,
    };
  },

  publishCommunityTemplate: async (
    data: PublishCommunityTemplateRequest,
  ): Promise<CommunityTemplateDetailResponse> => {
    const response = await callScribe(
      api.documentTemplate.publishCommunity({
        TemplateID: data.template_id,
        Description: data.description ?? '',
        Specialty: data.specialty,
        Category: data.category,
        Location: data.location,
        Tags: data.tags ?? [],
      }),
    );

    return communityTemplateToDomain(response.Template);
  },

  unpublishCommunityTemplate: async (id: string): Promise<void> => {
    await callScribe(
      api.documentTemplate.unpublishCommunity({ TemplateID: id }),
    );
  },

  takeCommunityVersion: async ({
    templateId,
    communityTemplateId,
  }: CommunityVersionRequest): Promise<DocumentTemplate> => {
    const response = await callScribe(
      api.documentTemplate.takeCommunityVersion({
        TemplateID: templateId,
        CommunityTemplateID: communityTemplateId,
      }),
    );

    return documentTemplateToDomain(response.DocumentTemplate);
  },

  dismissCommunityVersion: async ({
    templateId,
    communityTemplateId,
  }: CommunityVersionRequest): Promise<void> => {
    await callScribe(
      api.documentTemplate.dismissCommunityVersion({
        TemplateID: templateId,
        CommunityTemplateID: communityTemplateId,
      }),
    );
  },
};
